import { ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import { Media } from "~/Media";
import { NotApplicable } from "~/NotApplicable";
import * as Shadow from "~/Shadow";
import * as Typography from "~/Typography";

export function Image(props: Image.Props) {
  return <Image.Container {...props} />;
}

export namespace Image {
  export type Props = { readonly image: Media.Image };

  export const Container = styled.div`
    height: 300px;
    background-image: url("${({ image: { uri } }: Props) => uri}");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
  `;
}

export function Images({ images }: { readonly images: Media.Images }) {
  const [imageIndex, setImageIndex] = React.useState(0);
  const carousel = React.useRef<{ readonly goTo: (index: number) => void }>(
    null
  );

  React.useEffect(() => carousel.current?.goTo(imageIndex), [
    carousel,
    imageIndex,
  ]);

  return !images[0] ? (
    <Layout.Block.Component bottom>
      <Typography.H4.Component bottom>Images</Typography.H4.Component>
      <div style={{ textAlign: "center" }}>
        <NotApplicable>No images available</NotApplicable>
      </div>
    </Layout.Block.Component>
  ) : (
    pipe(
      images,
      ReadonlyArray.mapWithIndex((index, image) => (
        <Image key={index} image={image} />
      )),
      (images) => (
        <>
          <Images.Title>
            <Typography.H4.Component bottom>Images</Typography.H4.Component>
            <Images.Count>
              {imageIndex + 1} of {images.length}
            </Images.Count>
          </Images.Title>
          <Images.Container>
            <Ant.Carousel
              dots={false}
              ref={carousel}
              afterChange={setImageIndex}
            >
              {images}
            </Ant.Carousel>
            {images[1] && (
              <>
                {imageIndex > 0 && (
                  <Images.Arrow
                    direction="previous"
                    onClick={() =>
                      carousel.current?.goTo(Math.max(0, imageIndex - 1))
                    }
                  >
                    <Icon.ChevronLeft />
                  </Images.Arrow>
                )}
                {imageIndex < images.length - 1 && (
                  <Images.Arrow
                    direction="next"
                    onClick={() =>
                      carousel.current?.goTo(
                        Math.min(imageIndex + 1, images.length - 1)
                      )
                    }
                  >
                    <Icon.ChevronRight />
                  </Images.Arrow>
                )}
              </>
            )}
          </Images.Container>
        </>
      )
    )
  );
}

export namespace Images {
  export const Title = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: ${Layout.Block.padding()};
  `;

  export const Count = styled.div`
    align-self: flex-end;
  `;

  export const Container = styled.div`
    position: relative;
    padding-bottom: ${Layout.Block.padding()};
  `;

  export const Arrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    transform: translateY(-50%);
    position: absolute;
    top: 50%;
    ${({ direction }: { readonly direction: "previous" | "next" }) =>
      `${
        direction === "previous" ? "left" : "right"
      }: ${Layout.Inline.padding()};`}

    height: 50px;
    width: 50px;

    background: ${() => Color.almostWhite()};
    border: solid 1px ${() => Color.veryWhiteGrey()};
    border-radius: 100%;

    ${Shadow.Box.styles(Shadow.medium())}

    font-size: 1.5em;
  `;
}
